import * as React from 'react'
import {
  BooleanField,
  BooleanInput,
  CreateButton,
  Create,
  Datagrid,
  DateField,
  DeleteButton,
  Edit,
  EditButton,
  ListButton,
  List,
  NumberField,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  TopToolbar,
  useDataProvider,
  useNotify,
  useRecordContext,
  useRedirect,
  useRefresh,
} from 'react-admin'
import { useFormContext, useWatch } from 'react-hook-form'
import {
  Box,
  Button,
  MenuItem,
  Paper,
  TextField as MuiTextField,
  Typography,
} from '@mui/material'

const PROMPT_KEY_CHOICES = [
  {
    id: 'document_inventory_completeness',
    name: '01 - Document Inventory & Completeness',
  },
  {
    id: 'business_spend_categorisation',
    name: '02 - Business Spend Categorisation',
  },
  {
    id: 'core_contract_metadata_structure',
    name: '03 - Core Contract Metadata & Structure',
  },
  { id: 'classify_and_identify', name: 'Classify + Identify' },
  { id: 'check_precedence', name: 'Check Precedence' },
  { id: 'deep_extraction', name: 'Deep Extraction' },
  {
    id: 'core_commercial_legal_terms',
    name: '05 - Core Commercial & Legal Terms',
  },
  {
    id: 'software_ict_saas_licence',
    name: '06 - Software / ICT / SaaS / Licence',
  },
  { id: 'professional_services', name: '07 - Professional Services' },
  {
    id: 'managed_services_outsourcing',
    name: '08 - Managed Services / Outsourcing',
  },
  { id: 'goods_hardware_supply', name: '09 - Goods / Hardware / Supply' },
  {
    id: 'works_construction_property',
    name: '10 - Works / Construction / Property',
  },
  { id: 'data_protection_security', name: '11 - Data Protection & Security' },
  {
    id: 'sla_support_service_credits',
    name: '12 - SLA / Support / Service Credits',
  },
  {
    id: 'business_continuity_resilience_dr',
    name: '13 - Business Continuity / Resilience / DR',
  },
  {
    id: 'exit_transition_termination_assistance',
    name: '14 - Exit / Transition / Termination Assistance',
  },
  {
    id: 'clause_presence_absence_validator',
    name: '15 - Clause Presence / Absence Validator',
  },
  { id: 'client_specific_datasheet', name: '16 - Client-Specific Datasheet' },
  {
    id: 'cross_model_reconciliation_hallucination_checker',
    name: '17 - Cross-Model Reconciliation / Hallucination Checker',
  },
  {
    id: 'final_review_summary_output_formatter',
    name: '18 - Final Review Summary Formatter',
  },
]

const MODEL_CHOICES = [
  { id: '', name: 'Default org model' },
  { id: 'gemini-1.5-flash', name: 'Gemini 1.5 Flash' },
  { id: 'gemini-1.5-pro', name: 'Gemini 1.5 Pro' },
  { id: 'gpt-4o', name: 'GPT-4o' },
  { id: 'gpt-4.1', name: 'GPT-4.1' },
  { id: 'claude-sonnet-4-6', name: 'Claude Sonnet 4.6' },
]

const PromptActions = () => (
  <TopToolbar>
    <CreateButton />
  </TopToolbar>
)

const PromptEditActions = () => (
  <TopToolbar>
    <ListButton />
    <DeleteButton mutationMode="pessimistic" />
  </TopToolbar>
)

const PromptVersionActions = () => {
  const record = useRecordContext()
  const dataProvider = useDataProvider() as any
  const notify = useNotify()
  const refresh = useRefresh()
  const redirect = useRedirect()
  const { setValue } = useFormContext()
  const draftName = useWatch({ name: 'name' })
  const draftDescription = useWatch({ name: 'description' })
  const draftSystemMessage = useWatch({ name: 'system_message' })
  const draftUserPromptTemplate = useWatch({ name: 'user_prompt_template' })
  const draftPromptKey = useWatch({ name: 'prompt_key' })
  const draftEditMode = Boolean(useWatch({ name: '__draft_edit_mode' }))
  const [busy, setBusy] = React.useState(false)

  React.useEffect(() => {
    if (!record?.id) return
    setValue('__draft_edit_mode', false, { shouldDirty: false })
  }, [record?.id, setValue])

  if (!record) return null

  const hasTextDraftChanges =
    (draftSystemMessage ?? '') !== (record.system_message ?? '') ||
    (draftUserPromptTemplate ?? '') !== (record.user_prompt_template ?? '')

  const activate = () => {
    if (hasTextDraftChanges) {
      notify('Create a new version from this draft before activation', {
        type: 'warning',
      })
      return
    }
    setBusy(true)
    dataProvider
      .activateContractPrompt(record.id)
      .then(() => {
        notify('Prompt version activated', { type: 'info' })
        refresh()
      })
      .catch((error: any) => {
        notify(error?.message || 'Failed to activate prompt', {
          type: 'warning',
        })
      })
      .finally(() => setBusy(false))
  }

  const clone = () => {
    setBusy(true)
    dataProvider
      .create('contract-ai-prompts', {
        data: {
          prompt_key: draftPromptKey || record.prompt_key,
          base_version_id: record.id,
          name: draftName || undefined,
          description: draftDescription || undefined,
          system_message: draftSystemMessage || record.system_message,
          user_prompt_template:
            draftUserPromptTemplate || record.user_prompt_template,
          is_active: false,
        },
      })
      .then((created: any) => {
        const newId = created?.data?.id ?? created?.id
        notify('New prompt version created from current draft', {
          type: 'info',
        })
        if (newId) {
          redirect('edit', 'contract-ai-prompts', newId)
        } else {
          refresh()
        }
      })
      .catch((error: any) => {
        notify(error?.message || 'Failed to clone prompt', { type: 'warning' })
      })
      .finally(() => setBusy(false))
  }

  return (
    <Box sx={{ mt: 1, mb: 2 }}>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          onClick={() =>
            setValue('__draft_edit_mode', true, { shouldDirty: false })
          }
          disabled={busy || draftEditMode || hasTextDraftChanges}
        >
          Make and Test Changes
        </Button>
        <Button
          variant="outlined"
          onClick={clone}
          disabled={busy || !hasTextDraftChanges}
        >
          New Version From Draft
        </Button>
        <Button
          variant="contained"
          onClick={activate}
          disabled={busy || record.is_active || hasTextDraftChanges}
        >
          {record.is_active ? 'Active Version' : 'Activate Version'}
        </Button>
      </Box>
      {hasTextDraftChanges ? (
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ display: 'block', mt: 0.75 }}
        >
          Draft changes detected. Create a new version from draft to continue.
        </Typography>
      ) : draftEditMode ? (
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ display: 'block', mt: 0.75 }}
        >
          Edit prompt text and run tests. New Version From Draft will unlock
          after text changes.
        </Typography>
      ) : (
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ display: 'block', mt: 0.75 }}
        >
          Prompt text is read-only. Use Make and Test Changes to start a draft.
        </Typography>
      )}
    </Box>
  )
}

const PromptTester = () => {
  const record = useRecordContext()
  const dataProvider = useDataProvider() as any
  const notify = useNotify()
  const formSystemMessage = useWatch({ name: 'system_message' })
  const formUserPromptTemplate = useWatch({ name: 'user_prompt_template' })
  const formPromptKey = useWatch({ name: 'prompt_key' })

  const [testText, setTestText] = React.useState('')
  const [modelName, setModelName] = React.useState('')
  const [contractType, setContractType] = React.useState('Unknown')
  const [category, setCategory] = React.useState('General')
  const [precedenceHierarchy, setPrecedenceHierarchy] =
    React.useState('None identified')
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null)
  const [showAdvancedContext, setShowAdvancedContext] = React.useState(false)
  const [result, setResult] = React.useState<any>(null)
  const [busy, setBusy] = React.useState(false)

  if (!record) return null

  const activePromptKey = (formPromptKey || record.prompt_key) as string
  const activeSystemMessage = (formSystemMessage ||
    record.system_message) as string
  const activeUserPromptTemplate = (formUserPromptTemplate ||
    record.user_prompt_template) as string

  const template = (activeUserPromptTemplate || '') as string
  const needsContractType = template.includes('{contract_type}')
  const needsCategory = template.includes('{category}')
  const needsPrecedence = template.includes('{precedence_hierarchy}')

  const runTest = () => {
    if (!selectedFile && !testText.trim()) {
      notify('Add sample text or select a source file for prompt test', {
        type: 'warning',
      })
      return
    }
    setBusy(true)
    const basePayload = {
      prompt_key: activePromptKey,
      system_message: activeSystemMessage,
      user_prompt_template: activeUserPromptTemplate,
      model: modelName || undefined,
      contract_type: contractType,
      category,
      precedence_hierarchy: precedenceHierarchy,
    }
    const runPromise = selectedFile
      ? dataProvider.testContractPromptFile({
          ...basePayload,
          file: selectedFile,
        })
      : dataProvider.testContractPrompt({
          ...basePayload,
          test_text: testText,
        })

    runPromise
      .then((response: any) => {
        setResult(response)
      })
      .catch((error: any) => {
        notify(error?.message || 'Prompt test failed', { type: 'warning' })
      })
      .finally(() => setBusy(false))
  }

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Prompt Test
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
        Tests use the current editor content (including unsaved edits).
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
        Prompt versions are immutable. Use New Version From Draft to persist
        changes.
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
        Use a source file (`.pdf`, `.txt`, `.md`) or paste contract text
        directly.
      </Typography>
      <MuiTextField
        label="Model"
        value={modelName}
        onChange={(event) => setModelName(event.target.value)}
        select
        fullWidth
        size="small"
        margin="normal"
      >
        {MODEL_CHOICES.map((choice) => (
          <MenuItem key={choice.id || 'default'} value={choice.id}>
            {choice.name}
          </MenuItem>
        ))}
      </MuiTextField>
      <Box sx={{ mb: 2 }}>
        <Button variant="outlined" component="label">
          {selectedFile
            ? `Using file: ${selectedFile.name}`
            : 'Choose Source File'}
          <input
            hidden
            type="file"
            accept=".pdf,.txt,.md,.json,text/plain,application/pdf"
            onChange={(event) => {
              const nextFile = event.target.files?.[0] || null
              setSelectedFile(nextFile)
            }}
          />
        </Button>
        {selectedFile && (
          <Button sx={{ ml: 1 }} onClick={() => setSelectedFile(null)}>
            Remove File
          </Button>
        )}
      </Box>
      <TextInput
        source="__test_text"
        label="Sample Contract Text"
        helperText="Used when no source file is selected."
        multiline
        minRows={8}
        value={testText}
        onChange={(event) => setTestText(event.target.value)}
        fullWidth
      />
      {(needsContractType || needsCategory || needsPrecedence) && (
        <Box sx={{ mt: 1, mb: 1 }}>
          <Button
            size="small"
            onClick={() => setShowAdvancedContext((value) => !value)}
          >
            {showAdvancedContext
              ? 'Hide template context'
              : 'Show template context'}
          </Button>
        </Box>
      )}
      {showAdvancedContext && needsContractType && (
        <TextInput
          source="__test_contract_type"
          label="Contract Type Context"
          value={contractType}
          onChange={(event) => setContractType(event.target.value)}
          fullWidth
        />
      )}
      {showAdvancedContext && needsCategory && (
        <TextInput
          source="__test_category"
          label="Category Context"
          value={category}
          onChange={(event) => setCategory(event.target.value)}
          fullWidth
        />
      )}
      {showAdvancedContext && needsPrecedence && (
        <TextInput
          source="__test_precedence_hierarchy"
          label="Precedence Context"
          value={precedenceHierarchy}
          onChange={(event) => setPrecedenceHierarchy(event.target.value)}
          fullWidth
        />
      )}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button variant="contained" onClick={runTest} disabled={busy}>
          Run Test
        </Button>
      </Box>
      {result ? (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Result
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              backgroundColor: '#fafafa',
              maxHeight: 460,
              overflow: 'auto',
            }}
          >
            <pre
              style={{
                margin: 0,
                fontSize: 12,
                lineHeight: 1.4,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {JSON.stringify(result, null, 2)}
            </pre>
          </Paper>
        </Box>
      ) : null}
    </Paper>
  )
}

const PromptVersionSwitcher = () => {
  const record = useRecordContext()
  const dataProvider = useDataProvider() as any
  const redirect = useRedirect()
  const [choices, setChoices] = React.useState<any[]>([])
  const [loading, setLoading] = React.useState(false)

  React.useEffect(() => {
    if (!record?.prompt_key) return

    setLoading(true)
    dataProvider
      .getList('contract-ai-prompts', {
        pagination: { page: 1, perPage: 200 },
        sort: { field: 'version', order: 'DESC' },
        filter: {
          prompt_key: record.prompt_key,
          include_inactive: true,
        },
      })
      .then((response: any) => {
        setChoices(Array.isArray(response?.data) ? response.data : [])
      })
      .finally(() => setLoading(false))
  }, [dataProvider, record?.prompt_key, record?.id, record?.is_active])

  if (!record) return null

  return (
    <MuiTextField
      select
      label="Version"
      value={record.id}
      onChange={(event) => {
        const nextId = Number(event.target.value)
        if (nextId && nextId !== record.id) {
          redirect('edit', 'contract-ai-prompts', nextId)
        }
      }}
      fullWidth
      size="small"
      margin="normal"
      disabled={loading || choices.length === 0}
      helperText="Switch versions for this prompt stage"
    >
      {choices.map((row: any) => (
        <MenuItem key={row.id} value={row.id}>
          v{row.version} {row.is_active ? '(active)' : ''}{' '}
          {row.name ? `- ${row.name}` : ''}
        </MenuItem>
      ))}
    </MuiTextField>
  )
}

const PromptActiveStatus = () => {
  const record = useRecordContext()
  if (!record) return null
  return (
    <MuiTextField
      label="Active"
      value={record.is_active ? 'Yes' : 'No'}
      fullWidth
      size="small"
      margin="normal"
      disabled
    />
  )
}

const PromptTextEditorFields = () => {
  const record = useRecordContext()
  const draftEditMode = Boolean(useWatch({ name: '__draft_edit_mode' }))
  const draftSystemMessage = useWatch({ name: 'system_message' })
  const draftUserPromptTemplate = useWatch({ name: 'user_prompt_template' })

  if (!record) return null

  const hasTextDraftChanges =
    (draftSystemMessage ?? '') !== (record.system_message ?? '') ||
    (draftUserPromptTemplate ?? '') !== (record.user_prompt_template ?? '')
  const textEditable = draftEditMode || hasTextDraftChanges

  return (
    <>
      <MuiTextField
        label="Fixed Output JSON Contract"
        value={JSON.stringify(record.output_contract || {}, null, 2)}
        multiline
        minRows={10}
        fullWidth
        margin="normal"
        InputProps={{ readOnly: true }}
        helperText="This schema is fixed server-side and not editable in prompt text."
      />
      <TextInput
        source="system_message"
        multiline
        minRows={4}
        fullWidth
        disabled={!textEditable}
      />
      <TextInput
        source="user_prompt_template"
        multiline
        minRows={14}
        fullWidth
        helperText="Prompt body only. JSON output contract is fixed separately."
        disabled={!textEditable}
      />
    </>
  )
}

export const ContractAIPromptList = () => (
  <List sort={{ field: 'id', order: 'DESC' }} actions={<PromptActions />}>
    <Datagrid rowClick="edit" bulkActionButtons={false}>
      <TextField source="prompt_key" label="Prompt Stage" />
      <NumberField source="version" />
      <TextField source="name" />
      <BooleanField source="is_active" />
      <DateField source="updated_at" showTime />
      <EditButton />
      <DeleteButton mutationMode="pessimistic" />
    </Datagrid>
  </List>
)

export const ContractAIPromptEdit = () => (
  <Edit actions={<PromptEditActions />}>
    <SimpleForm toolbar={false}>
      <TextInput source="id" disabled />
      <TextInput source="prompt_key" disabled />
      <TextInput source="version" disabled />
      <PromptVersionSwitcher />
      <TextInput source="name" fullWidth />
      <TextInput source="description" fullWidth multiline minRows={2} />
      <PromptActiveStatus />
      <PromptTextEditorFields />
      <PromptVersionActions />
      <PromptTester />
    </SimpleForm>
  </Edit>
)

export const ContractAIPromptCreate = () => (
  <Create>
    <SimpleForm>
      <SelectInput source="prompt_key" choices={PROMPT_KEY_CHOICES} fullWidth />
      <TextInput source="name" fullWidth />
      <TextInput source="description" fullWidth multiline minRows={2} />
      <BooleanInput source="is_active" />
      <TextInput source="system_message" multiline minRows={4} fullWidth />
      <TextInput
        source="user_prompt_template"
        multiline
        minRows={14}
        fullWidth
      />
    </SimpleForm>
  </Create>
)
